/* ============================================================
   Watch Reserves — welcome offer popup.

   Shows once per visitor, after they have shown some engagement, and
   trades an email for a single-use discount code.

   Deliberately not on page load: Shopify's performance guidance treats an
   immediate dialog as an anti-pattern — Google penalises intrusive
   interstitials on mobile and the dialog competes with LCP. Waiting for
   scroll depth or dwell time also means the visitors who see it are the
   ones who stayed.

   Two things happen on submit, and they are independent on purpose:
   Klaviyo gets the subscription, the Railway relay mints the code. If
   Klaviyo is blocked — ad blockers reach it often — the visitor still
   gets their code, because losing the sale to protect the mailing list is
   the wrong trade.
   ============================================================ */
(function () {
  'use strict';

  var dialog = document.getElementById('wr-welcome-offer');
  if (!dialog) return;

  var SEEN_KEY = 'wr-offer-seen';
  var CODE_KEY = 'wr-offer-code';

  function stored(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function store(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  // Already subscribed, dismissed, or holding a code: never ask again.
  if (stored(SEEN_KEY)) return;

  var form = dialog.querySelector('[data-wr-offer-form]');
  var input = dialog.querySelector('#wr-offer-email');
  var button = dialog.querySelector('[data-wr-offer-submit]');
  var note = dialog.querySelector('[data-wr-offer-note]');
  var ask = dialog.querySelector('[data-wr-offer-ask]');
  var done = dialog.querySelector('[data-wr-offer-done]');
  var codeEl = dialog.querySelector('[data-wr-offer-code]');
  var termsEl = dialog.querySelector('[data-wr-offer-terms]');
  var busy = false;

  function say(message) {
    if (!note) return;
    note.textContent = message || '';
    note.hidden = !message;
  }

  /* ── Trigger ─────────────────────────────────────────── */
  var opened = false;

  function open() {
    if (opened || dialog.open) return;
    // Don't interrupt someone mid-checkout-decision with the cart open.
    var cart = document.getElementById('cart-drawer');
    if (cart && cart.open) return;
    opened = true;
    dialog.showModal();
    teardown();
  }

  function scrolled() {
    var h = document.documentElement;
    var max = h.scrollHeight - h.clientHeight;
    if (max <= 0) return 0;
    return ((window.scrollY || h.scrollTop) / max) * 100;
  }

  var threshold = Number(dialog.dataset.scroll) || 45;
  var delay = (Number(dialog.dataset.delay) || 15) * 1000;

  function onScroll() {
    if (scrolled() >= threshold) open();
  }

  var timer = window.setTimeout(open, delay);
  window.addEventListener('scroll', onScroll, { passive: true });

  function teardown() {
    window.clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
  }

  // Dismissing counts as an answer — don't ask again on the next page.
  dialog.addEventListener('close', function () {
    store(SEEN_KEY, '1');
  });

  /* ── Klaviyo ─────────────────────────────────────────── */
  function subscribe(email) {
    var company = dialog.dataset.company;
    if (!company) return Promise.resolve();
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + company, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2025-01-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: dialog.dataset.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: dialog.dataset.list } } }
        }
      })
    }).catch(function () { /* see header: never block the code on this */ });
  }

  /* ── Reveal ──────────────────────────────────────────── */
  function reveal(payload) {
    if (codeEl) codeEl.textContent = payload.code;
    if (termsEl) {
      termsEl.textContent =
        '$' + payload.amount + ' off orders over $' + payload.minimum +
        '. One use. Tap the code to copy it again.';
    }
    if (ask) ask.hidden = true;
    if (done) done.hidden = false;

    store(SEEN_KEY, '1');
    store(CODE_KEY, payload.code);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(payload.code).catch(function () { /* needs a gesture in some browsers */ });
    }
    if (window.wrAnnounce) window.wrAnnounce('Your discount code is ' + payload.code);
  }

  if (codeEl) {
    codeEl.addEventListener('click', function () {
      if (!navigator.clipboard || !codeEl.textContent) return;
      navigator.clipboard.writeText(codeEl.textContent).then(function () {
        var was = codeEl.textContent;
        codeEl.textContent = 'Copied';
        window.setTimeout(function () { codeEl.textContent = was; }, 1200);
      }).catch(function () { /* nothing useful to say if it fails */ });
    });
  }

  /* ── Submit ──────────────────────────────────────────── */
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;

      var email = (input && input.value || '').trim();
      if (!email || email.indexOf('@') === -1 || email.indexOf('.') === -1) {
        say('Please enter a valid email address.');
        if (input) input.focus();
        return;
      }

      var honeypot = form.querySelector('input[name="company"]');
      busy = true;
      say('');
      if (button) { button.disabled = true; button.textContent = '...'; }

      subscribe(email);

      fetch(dialog.dataset.endpoint.replace(/\/$/, '') + '/discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, company: honeypot ? honeypot.value : '' })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok || !res.body || !res.body.code) {
            throw new Error((res.body && res.body.error) || 'No code returned');
          }
          reveal(res.body);
        })
        .catch(function () {
          // The subscription above may well have succeeded, so don't imply
          // they need to start over — just that the code is coming by email.
          say('You are on the list. Your code is on its way by email.');
          store(SEEN_KEY, '1');
        })
        .finally(function () {
          busy = false;
          if (button) { button.disabled = false; button.textContent = 'Claim'; }
        });
    });
  }
})();
